import { GmailService } from './gmailService';
import { communicationService } from './enhancedDatabaseService';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { PreApproval, PreApprovalData } from '@/types/pre-approval';
import { formatSmartGreeting, formatCombinedGreeting } from '@/utils/pre-approval/greetingHelpers';
import {
  formatPreApprovalExpirationDate,
  resolveCanonicalPreApprovalExpiresAt
} from '@/utils/pre-approval/expiration';

const log = createComponentLogger('EmailService', 'service');

interface EmailSendResult {
  success: boolean;
  communicationId?: string;
  messageId?: string;
  error?: string;
}

interface PreApprovalEmailOptions {
  recipientType?: 'borrower' | 'co_borrower' | 'partner';
  recipientName?: string;
}

export class EmailService {
  /**
   * Send pre-approval email with communication tracking
   */
  static async sendPreApprovalEmail(
    preApproval: PreApproval | PreApprovalData,
    recipientEmail: string,
    message: string,
    previewUrl: string,
    ccEmails?: string,
    options: PreApprovalEmailOptions = {}
  ): Promise<EmailSendResult> {
    try {
      // Validate required parameters
      if (!recipientEmail || recipientEmail.trim() === '') {
        log.error('SendPreApprovalEmail called with empty/invalid recipient email', {
          recipientEmail,
          recipientType: typeof recipientEmail,
          preApprovalId: preApproval?.id
        });
        throw new Error('Recipient email address is required and cannot be empty');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(recipientEmail.trim())) {
        log.error('SendPreApprovalEmail called with invalid email format', {
          recipientEmail,
          preApprovalId: preApproval?.id
        });
        throw new Error(`Invalid email address format: ${recipientEmail}`);
      }

      log.info('Starting pre-approval email send', {
        preApprovalId: preApproval.id,
        recipientEmail,
        hasCc: !!ccEmails,
        messageLength: message?.length || 0,
        expirationDate: preApproval.expirationDate
      });

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        log.error('No authenticated user found when sending pre-approval email');
        throw new Error('No authenticated user');
      }

      // Handle both PreApproval and PreApprovalData types
      const borrowerName = 'borrowerInfo' in preApproval 
        ? `${preApproval.borrowerInfo.firstName} ${preApproval.borrowerInfo.lastName}`
        : preApproval.borrowerName;
      
      const approvalAmount = 'loanDetails' in preApproval
        ? preApproval.loanDetails.loanAmount
        : preApproval.approvalAmount;
      
      const borrowerId = 'borrowerId' in preApproval
        ? preApproval.borrowerId
        : preApproval.contactId || null;

      // Log the communication as pending
      const communication = await communicationService.logCommunication({
        user_id: user.id,
        loan_journey_id: null, // Pre-approvals might not have journey yet
        borrower_id: borrowerId,
        prospect_id: null,
        type: 'email',
        direction: 'outbound',
        subject: `Pre-Approval Letter - ${borrowerName}`,
        content: message,
        email_address: recipientEmail,
        status: 'pending',
        metadata: {
          pre_approval_id: preApproval.id,
          preview_url: previewUrl,
          approval_amount: approvalAmount,
          expiration_date: preApproval.expirationDate,
          channel: 'gmail_preapproval',
          recipient_type: options.recipientType || 'borrower',
          recipient_name: options.recipientName || ''
        }
      });

      try {
        // Add tracking parameter to preview URL
        const trackedUrl = `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}cid=${communication.id}`;
        
        // Create email HTML
        const htmlBody = EmailService.createPreApprovalEmailHtml(
          preApproval,
          trackedUrl,
          message,
          options
        );

        // Send the actual email
        await GmailService.sendEmail({
          to: recipientEmail,
          cc: ccEmails,
          subject: `Your Pre-Approval Letter from ${user.user_metadata?.full_name || 'Your Loan Officer'}`,
          body: message,
          htmlBody
        });

        // Update communication status to sent
        await communicationService.updateCommunicationStatus(
          communication.id,
          'sent',
          new Date()
        );

        return {
          success: true,
          communicationId: communication.id,
          messageId: communication.id
        };
      } catch (emailError) {
        // Update communication status to failed
        await communicationService.updateCommunicationStatus(
          communication.id,
          'failed',
          new Date()
        );

        throw emailError;
      }
    } catch (error) {
      log.error('Failed to send pre-approval email', { error: error instanceof Error ? error.message : String(error) });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email'
      };
    }
  }

  /**
   * Send loan officer confirmation email (separate from borrower email)
   * This prevents LO views from marking the pre-approval as "viewed by borrower"
   */
  static async sendLoanOfficerConfirmation(
    preApproval: PreApproval | PreApprovalData,
    borrowerEmail: string
  ): Promise<EmailSendResult> {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !user.email) {
        log.warn('No authenticated user or email found for LO confirmation');
        return { success: false, error: 'No user email found' };
      }

      // Handle both PreApproval and PreApprovalData types
      const borrowerName = 'borrowerInfo' in preApproval
        ? `${preApproval.borrowerInfo.firstName} ${preApproval.borrowerInfo.lastName}`
        : preApproval.borrowerName;

      const approvalAmount = 'loanDetails' in preApproval
        ? preApproval.loanDetails.loanAmount
        : preApproval.approvalAmount;

      // Create LO preview link (isolated, doesn't track as borrower view)
      const dashboardUrl = `${window.location.origin}/pre-approval/${preApproval.id}/preview?lo=true`;

      // Create HTML for LO confirmation email
      const htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Pre-Approval Sent Confirmation</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background-color: white;
      border-radius: 8px;
      padding: 30px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      text-align: center;
      margin-bottom: 20px;
      padding-bottom: 20px;
      border-bottom: 2px solid #183C2E;
    }
    .logo {
      font-size: 20px;
      font-weight: bold;
      color: #183C2E;
    }
    .status-badge {
      display: inline-block;
      background-color: #10b981;
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: bold;
      margin: 10px 0;
    }
    .button {
      display: inline-block;
      background-color: #183C2E;
      color: white !important;
      text-decoration: none;
      padding: 12px 24px;
      border-radius: 6px;
      font-weight: bold;
      margin: 15px 0;
    }
    .info-box {
      background-color: #f8f9fa;
      padding: 15px;
      border-radius: 6px;
      margin: 15px 0;
    }
    .footer {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      text-align: center;
      font-size: 12px;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo">Lendwise Mortgage</div>
      <div class="status-badge">✓ Pre-Approval Sent</div>
    </div>

    <h2>Confirmation: Pre-Approval Delivered</h2>

    <p>Your pre-approval letter has been successfully sent to:</p>

    <div class="info-box">
      <strong>Borrower:</strong> ${borrowerName}<br>
      <strong>Email:</strong> ${borrowerEmail}<br>
      <strong>Amount:</strong> $${approvalAmount?.toLocaleString() || 'N/A'}
    </div>

    <p>The borrower will receive an email with a secure link to view and download their pre-approval letter.</p>

    <p style="text-align: center;">
      <a href="${dashboardUrl}" class="button">View in Dashboard</a>
    </p>

    <p style="font-size: 14px; color: #666;">
      <strong>Next Steps:</strong>
    </p>
    <ul style="font-size: 14px; color: #666;">
      <li>Monitor for borrower activity in your dashboard</li>
      <li>You'll be notified when the borrower views the letter</li>
      <li>Follow up if needed after the borrower has reviewed</li>
    </ul>

    <div class="footer">
      <p>This is an automated confirmation message.</p>
      <p>© ${new Date().getFullYear()} Lendwise Mortgage. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;

      // Send the email
      await GmailService.sendEmail({
        to: user.email,
        subject: `✓ Pre-Approval Sent: ${borrowerName}`,
        body: `Your pre-approval letter for ${borrowerName} ($${approvalAmount?.toLocaleString()}) has been sent successfully.`,
        htmlBody
      });

      log.info('LO confirmation email sent', {
        preApprovalId: preApproval.id,
        loanOfficerEmail: user.email,
        borrowerName
      });

      return {
        success: true
      };
    } catch (error) {
      log.error('Failed to send LO confirmation email', {
        error: error instanceof Error ? error.message : String(error),
        preApprovalId: preApproval.id
      });
      // Don't throw - LO confirmation is non-critical
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send confirmation'
      };
    }
  }

  /**
   * Create pre-approval email HTML template
   */
  private static createPreApprovalEmailHtml(
    preApproval: PreApproval | PreApprovalData,
    previewUrl: string,
    message: string,
    options: PreApprovalEmailOptions = {}
  ): string {
    // Handle both PreApproval and PreApprovalData types
    const borrowerInfo = 'borrowerInfo' in preApproval ? preApproval.borrowerInfo : null;

    const recipientType = options.recipientType || 'borrower';
    const recipientName = options.recipientName?.trim();

    // Format name with salutation if available
    let borrowerName = '';
    let greeting = '';
    let heading = '';
    let introCopy = 'You have been pre-approved for a home loan:';
    let closingCopy = 'Your pre-approval letter is ready for download. You can share this with your real estate agent or use it when making offers on homes.';

    if (borrowerInfo) {
      // Full name for "Congratulations" line
      borrowerName = `${borrowerInfo.firstName} ${borrowerInfo.lastName}`;

      // Use combined greeting helper for consistent formatting with PDF/preview
      const coBorrowerInfo = 'coBorrowerInfo' in preApproval ? preApproval.coBorrowerInfo : null;
      if (recipientType === 'co_borrower') {
        greeting = `Dear ${recipientName || formatSmartGreeting(coBorrowerInfo || undefined)}`;
        heading = 'Your pre-approval letter is ready';
        introCopy = `${borrowerName} and you have been pre-approved for a home loan:`;
        closingCopy = 'The pre-approval letter is ready for download and can be used when making offers on homes.';
      } else if (recipientType === 'partner') {
        greeting = `Hi ${recipientName || 'Partner'}`;
        heading = `Pre-approval letter for ${borrowerName}`;
        introCopy = `${borrowerName} has been pre-approved for a home loan:`;
        closingCopy = 'The pre-approval letter is ready for your review and use while coordinating offers.';
      } else {
        greeting = `Dear ${formatCombinedGreeting(borrowerInfo, coBorrowerInfo || undefined)}`;
        heading = `Congratulations, ${borrowerName}!`;
      }
    } else {
      borrowerName = preApproval.borrowerName || 'Borrower';
      greeting = `Dear ${borrowerName}`;
      heading = `Congratulations, ${borrowerName}!`;
    }

    const approvalAmount = 'loanDetails' in preApproval
      ? preApproval.loanDetails.loanAmount || 0
      : preApproval.approvalAmount;

    // Get purchase price for prominent display
    const purchasePrice = 'loanDetails' in preApproval
      ? preApproval.loanDetails.purchasePrice || 0
      : 0;

    // Helper to format currency with decimals
    const formatAmount = (amount: number) =>
      amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your Pre-Approval Letter</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background-color: white;
      border-radius: 8px;
      padding: 40px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
    }
    .logo {
      font-size: 24px;
      font-weight: bold;
      color: #183C2E;
    }
    .content {
      margin-bottom: 30px;
    }
    .purchase-price {
      font-size: 42px;
      font-weight: 700;
      color: #183C2E;
      text-align: center;
      margin: 8px 0;
    }
    .loan-amount {
      font-size: 28px;
      font-weight: 600;
      color: #374151;
      text-align: center;
      margin: 8px 0;
    }
    .amount-label {
      margin: 0;
      color: #6b7280;
      font-size: 14px;
      text-align: center;
    }
    .button {
      display: inline-block;
      background-color: #183C2E;
      color: white;
      text-decoration: none;
      padding: 12px 30px;
      border-radius: 6px;
      font-weight: bold;
      text-align: center;
      margin: 20px 0;
    }
    .button:hover {
      background-color: #2a5a44;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      text-align: center;
      font-size: 14px;
      color: #666;
    }
    .message {
      background-color: #f8f9fa;
      padding: 20px;
      border-radius: 6px;
      margin: 20px 0;
      white-space: pre-wrap;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo">Lendwise Mortgage</div>
    </div>
    
    <div class="content">
      <p style="font-size: 18px; margin-bottom: 10px;">${greeting},</p>

      <h2 style="margin-top: 0;">${heading}</h2>

      <p>${introCopy}</p>

      <!-- Purchase Price - Primary, Large, Bold -->
      <div style="margin: 24px 0;">
        <p class="amount-label">Approved Purchase Price</p>
        <div class="purchase-price">$${formatAmount(purchasePrice)}</div>
      </div>

      <!-- Loan Amount - Secondary -->
      <div style="margin: 16px 0;">
        <p class="amount-label">Loan Amount</p>
        <div class="loan-amount">$${formatAmount(approvalAmount)}</div>
      </div>
      
      ${message ? `<div class="message">${message}</div>` : ''}
      
      <p style="text-align: center;">
        <a href="${previewUrl}" class="button">View Your Pre-Approval Letter</a>
      </p>

      <p style="text-align: center; color: #666; font-size: 14px;">
        ${(() => {
          const expirationDate = formatPreApprovalExpirationDate(
            resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate)
          );
          return expirationDate
            ? `This link expires on ${expirationDate}.`
            : 'Please contact your loan officer for link expiration details.';
        })()}
      </p>

      <p>${closingCopy}</p>

      <p><strong>Important Details:</strong></p>
      <ul>
        <li>Purchase price: $${formatAmount(purchasePrice)}</li>
        <li>Loan amount: $${formatAmount(approvalAmount)}</li>
        <li>Valid until: ${formatPreApprovalExpirationDate(resolveCanonicalPreApprovalExpiresAt(preApproval.expiresAt, preApproval.expirationDate)) || 'Please contact your loan officer for details'}</li>
        <li>Letter ID: ${preApproval.id}</li>
      </ul>
    </div>
    
    <div class="footer">
      <p>This is an automated message. Please do not reply to this email.</p>
      <p>© ${new Date().getFullYear()} Lendwise Mortgage. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
  }

  /**
   * Track email open event
   */
  static async trackEmailOpen(communicationId: string): Promise<void> {
    try {
      log.debug('Tracking email open', { communicationId });
      const result = await communicationService.updateCommunicationStatus(
        communicationId,
        'opened',
        new Date()
      );
      log.info('Email open tracked successfully', { communicationId, result });
    } catch (error) {
      log.error('Failed to track email open', { communicationId, error: error instanceof Error ? error.message : String(error) });
      throw error; // Re-throw to let caller handle
    }
  }

  /**
   * Track email click event
   */
  static async trackEmailClick(communicationId: string): Promise<void> {
    try {
      log.debug('Tracking email click', { communicationId });
      const result = await communicationService.updateCommunicationStatus(
        communicationId,
        'clicked',
        new Date()
      );
      log.info('Email click tracked successfully', { communicationId, result });
    } catch (error) {
      log.error('Failed to track email click', { communicationId, error: error instanceof Error ? error.message : String(error) });
      throw error; // Re-throw to let caller handle
    }
  }

  /**
   * Send recruiting invitation email
   */
  static async sendRecruitingInvitation(
    candidateEmail: string,
    candidateFirstName: string,
    message: string,
    inviteUrl: string,
    recruiterName: string,
    ccEmails?: string,
    communicationId?: string
  ): Promise<EmailSendResult> {
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(candidateEmail.trim())) {
        throw new Error(`Invalid email address format: ${candidateEmail}`);
      }

      log.info('Sending recruiting invitation email', {
        candidateEmail,
        candidateFirstName,
        hasCc: !!ccEmails,
        messageLength: message?.length || 0
      });

      // Use invite URL directly - no tracking params (breaks NMLS login)
      const trackedUrl = inviteUrl;

      // Create email HTML
      const htmlBody = EmailService.createRecruitingInvitationEmailHtml(
        candidateFirstName,
        trackedUrl,
        message,
        recruiterName
      );

      // Send the actual email via Gmail
      await GmailService.sendEmail({
        to: candidateEmail,
        cc: ccEmails,
        subject: `Join Our Team at Lendwise - ${recruiterName}`,
        body: message,
        htmlBody
      });

      log.info('Recruiting invitation email sent successfully', {
        candidateEmail,
        communicationId
      });

      return {
        success: true,
        communicationId
      };

    } catch (error) {
      log.error('Failed to send recruiting invitation email', {
        candidateEmail,
        error: error instanceof Error ? error.message : String(error)
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send invitation email'
      };
    }
  }

  /**
   * Send onboarding email (for candidates who have completed technology setup)
   */
  static async sendOnboardingEmail(
    candidateEmail: string,
    candidateFirstName: string,
    message: string,
    inviteUrl: string,
    recruiterName: string,
    ccEmails?: string,
    communicationId?: string,
    onboardingTasks: Array<{ id: string; title: string; status: string }> = [],
    credentials?: {
      gmail?: { account: string; tempPassword: string };
      optimal?: { username: string };
      encompass?: { userId: string; tempPassword: string };
    }
  ): Promise<EmailSendResult> {
    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(candidateEmail.trim())) {
        throw new Error(`Invalid email address format: ${candidateEmail}`);
      }

      log.info('Sending onboarding email', {
        candidateEmail,
        candidateFirstName,
        hasCc: !!ccEmails,
        messageLength: message?.length || 0
      });

      // Use invite URL directly - no tracking params (breaks NMLS login)
      const trackedUrl = inviteUrl;

      // Create email HTML
      const htmlBody = EmailService.createOnboardingEmailHtml(
        candidateFirstName,
        trackedUrl,
        message,
        recruiterName,
        onboardingTasks,
        credentials
      );

      // Send the actual email via Gmail
      await GmailService.sendEmail({
        to: candidateEmail,
        cc: ccEmails,
        subject: `Continue Your Onboarding at Lendwise - ${recruiterName}`,
        body: message,
        htmlBody
      });

      log.info('Onboarding email sent successfully', {
        candidateEmail,
        communicationId
      });

      return {
        success: true,
        communicationId
      };

    } catch (error) {
      log.error('Failed to send onboarding email', {
        candidateEmail,
        error: error instanceof Error ? error.message : String(error)
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send onboarding email'
      };
    }
  }

  /**
   * Create recruiting invitation email HTML template
   */
  private static createRecruitingInvitationEmailHtml(
    candidateFirstName: string,
    inviteUrl: string,
    message: string,
    recruiterName: string
  ): string {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Join Our Team at Lendwise</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background-color: white;
      border-radius: 8px;
      padding: 40px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #183C2E;
    }
    .logo {
      font-size: 28px;
      font-weight: bold;
      color: #183C2E;
      margin-bottom: 5px;
    }
    .tagline {
      font-size: 14px;
      color: #666;
      font-style: italic;
    }
    .content {
      margin-bottom: 30px;
    }
    .greeting {
      font-size: 20px;
      color: #183C2E;
      margin-bottom: 20px;
    }
    .message {
      background-color: #f0f8f5;
      padding: 20px;
      border-left: 4px solid #183C2E;
      border-radius: 4px;
      margin: 20px 0;
      white-space: pre-wrap;
    }
    .button-container {
      text-align: center;
      margin: 30px 0;
    }
    .button {
      display: inline-block;
      background-color: #183C2E;
      color: white !important;
      text-decoration: none;
      padding: 14px 36px;
      border-radius: 6px;
      font-weight: bold;
      font-size: 16px;
      text-align: center;
    }
    .button:hover {
      background-color: #2a5a44;
    }
    .steps {
      background-color: #f0f8f5;
      padding: 20px;
      border-radius: 6px;
      margin: 20px 0;
    }
    .steps h3 {
      color: #183C2E;
      margin-top: 0;
      font-size: 18px;
    }
    .steps ul {
      margin: 10px 0;
      padding-left: 20px;
    }
    .steps li {
      margin: 8px 0;
      color: #555;
    }
    .expiration-notice {
      text-align: center;
      color: #666;
      font-size: 13px;
      margin: 15px 0;
      font-style: italic;
    }
    .signature {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
    }
    .signature-name {
      font-weight: bold;
      color: #183C2E;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      text-align: center;
      font-size: 12px;
      color: #999;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo">Lendwise Mortgage</div>
      <div class="tagline">Building Careers, Creating Success</div>
    </div>

    <div class="content">
      <div class="message">${message}</div>

      <div class="button-container">
        <a href="${inviteUrl}" class="button">Complete Your Application</a>
      </div>

      <div class="steps">
        <h3>Next Steps - Complete Your Application:</h3>
        <ul>
          <li>Complete your professional profile</li>
          <li>Upload required documents (licenses, certifications)</li>
          <li>Review and sign your employment agreement</li>
        </ul>
      </div>

      <p class="expiration-notice">
        This invitation will expire in 14 days
      </p>

      <p>We're excited about the opportunity to have you join our team and look forward to working with you!</p>

      <p>If you have any questions, please don't hesitate to reach out.</p>

      <div class="signature">
        <p>Best regards,</p>
        <p class="signature-name">${recruiterName}</p>
        <p style="margin: 0; color: #666;">Lendwise Mortgage</p>
      </div>
    </div>

    <div class="footer">
      <p>This email was sent from a secure Lendwise account.</p>
      <p>© ${new Date().getFullYear()} Lendwise Mortgage. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
  }

  /**
   * Create onboarding email HTML template
   * Used for candidates who have completed technology setup (Tasks 1-4)
   */
  private static createOnboardingEmailHtml(
    candidateFirstName: string,
    inviteUrl: string,
    message: string,
    recruiterName: string,
    onboardingTasks: Array<{ id: string; title: string; status: string }> = [],
    credentials?: {
      gmail?: { account: string; tempPassword: string };
      optimal?: { username: string };
      encompass?: { userId: string; tempPassword: string };
    }
  ): string {
    // Filter tasks to get only incomplete ones from tasks 6-7 (Xactus and HR Forms)
    const incompleteTasks = onboardingTasks.filter(
      task => ['6', '7'].includes(task.id) && task.status !== 'completed'
    );
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Continue Your Onboarding at Lendwise</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f5f5f5;
    }
    .container {
      background-color: white;
      border-radius: 8px;
      padding: 40px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 2px solid #183C2E;
    }
    .logo {
      font-size: 28px;
      font-weight: bold;
      color: #183C2E;
      margin-bottom: 5px;
    }
    .tagline {
      font-size: 14px;
      color: #666;
      font-style: italic;
    }
    .status-badge {
      display: inline-block;
      background-color: #10b981;
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: bold;
      margin: 15px 0;
    }
    .content {
      margin-bottom: 30px;
    }
    .greeting {
      font-size: 20px;
      color: #183C2E;
      margin-bottom: 20px;
    }
    .message {
      background-color: #f0f8f5;
      padding: 20px;
      border-left: 4px solid #183C2E;
      border-radius: 4px;
      margin: 20px 0;
      white-space: pre-wrap;
    }
    .button-container {
      text-align: center;
      margin: 30px 0;
    }
    .button {
      display: inline-block;
      background-color: #183C2E;
      color: white !important;
      text-decoration: none;
      padding: 14px 36px;
      border-radius: 6px;
      font-weight: bold;
      font-size: 16px;
      text-align: center;
    }
    .button:hover {
      background-color: #2a5a44;
    }
    .progress-section {
      background-color: #f0f8f5;
      padding: 20px;
      border-radius: 6px;
      margin: 20px 0;
    }
    .progress-section h3 {
      color: #183C2E;
      margin-top: 0;
      font-size: 18px;
    }
    .checklist {
      margin: 10px 0;
      padding-left: 0;
      list-style: none;
    }
    .checklist li {
      margin: 10px 0;
      padding-left: 30px;
      position: relative;
      color: #555;
    }
    .checklist li.completed::before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #10b981;
      font-weight: bold;
      font-size: 18px;
    }
    .checklist li.pending::before {
      content: "○";
      position: absolute;
      left: 5px;
      color: #999;
      font-size: 14px;
    }
    .next-steps {
      background-color: #fff9e6;
      border-left: 4px solid #f59e0b;
      padding: 15px;
      border-radius: 4px;
      margin: 20px 0;
    }
    .next-steps h4 {
      color: #f59e0b;
      margin-top: 0;
      font-size: 16px;
    }
    .next-steps ul {
      margin: 10px 0;
      padding-left: 20px;
    }
    .next-steps li {
      margin: 8px 0;
      color: #555;
    }
    .signature {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
    }
    .signature-name {
      font-weight: bold;
      color: #183C2E;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      text-align: center;
      font-size: 12px;
      color: #999;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo">Lendwise Mortgage</div>
      <div class="tagline">Building Careers, Creating Success</div>
      <div class="status-badge">✓ Technology Setup Complete</div>
    </div>

    <div class="content">
      <p class="greeting">Hi ${candidateFirstName},</p>

      <div class="message">${message}</div>

      ${credentials ? `
      <div class="progress-section">
        <h3>Your Account Information</h3>
        <div style="background: white; padding: 15px; border-radius: 4px; border: 1px solid #d0d0d0;">
          ${credentials.gmail ? `
          <div style="margin-bottom: 12px;">
            <strong style="color: #183C2E;">Gmail Account:</strong><br>
            <span style="font-family: monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 3px;">${credentials.gmail.account}</span><br>
            <strong style="color: #666; font-size: 13px;">Temporary Password:</strong>
            <span style="font-family: monospace; background: #fff9e6; padding: 2px 6px; border-radius: 3px; border: 1px solid #f59e0b;">${credentials.gmail.tempPassword}</span>
          </div>
          ` : ''}
          ${credentials.optimal ? `
          <div style="margin-bottom: 12px;">
            <strong style="color: #183C2E;">Optimal Blue Username:</strong><br>
            <span style="font-family: monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 3px;">${credentials.optimal.username}</span><br>
            <em style="color: #666; font-size: 12px;">Password is linked to your Lendwise email address</em>
          </div>
          ` : ''}
          ${credentials.encompass ? `
          <div>
            <strong style="color: #183C2E;">Encompass LOS:</strong><br>
            <span style="font-family: monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 3px;">${credentials.encompass.userId}</span><br>
            <strong style="color: #666; font-size: 13px;">Temporary Password:</strong>
            <span style="font-family: monospace; background: #fff9e6; padding: 2px 6px; border-radius: 3px; border: 1px solid #f59e0b;">${credentials.encompass.tempPassword}</span><br>
            <strong style="color: #666; font-size: 13px;">Client ID:</strong>
            <span style="font-family: monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 3px;">BE11391385</span><br>
            <strong style="color: #666; font-size: 13px;">Download Encompass:</strong>
            <a href="https://download.elliemae.com/encompass/install/smartclient/enc360/EncompassDesktop.exe" style="color: #183C2E; text-decoration: underline;">EncompassDesktop.exe</a>
          </div>
          ` : ''}
        </div>
        <p style="font-size: 12px; color: #999; margin-top: 8px; font-style: italic;">
          Please change these temporary passwords upon your first login for security.
        </p>
      </div>
      ` : ''}

      <div class="progress-section">
        <h3>Your Onboarding Progress</h3>
        <ul class="checklist">
          <li class="completed">Gmail account assigned</li>
          <li class="completed">User account linked</li>
          <li class="completed">Optimal Blue account assigned</li>
          <li class="completed">Encompass account created</li>
          ${onboardingTasks.find(t => t.id === '6')?.status === 'completed'
            ? '<li class="completed">Xactus credentials requested</li>'
            : '<li class="pending">Xactus credentials requested</li>'}
          ${onboardingTasks.find(t => t.id === '7')?.status === 'completed'
            ? '<li class="completed">Identification documents uploaded</li>'
            : '<li class="pending">Identification documents uploaded</li>'}
        </ul>
      </div>

      <div class="button-container">
        <a href="${inviteUrl}" class="button">Continue Onboarding</a>
      </div>

      ${incompleteTasks.length > 0 ? `
      <div class="next-steps">
        <h4>Next Steps to Complete:</h4>
        <ul>
          ${incompleteTasks.map(task => {
            if (task.id === '6') {
              return '<li><strong>Request Xactus Credentials:</strong> Submit your request for billing verification credentials</li>';
            } else if (task.id === '7') {
              return '<li><strong>Upload Identification:</strong> Upload two forms of valid government-issued identification</li>';
            }
            return '';
          }).join('')}
          ${incompleteTasks.length === 0 ? '<li><strong>Final Review:</strong> Complete your onboarding checklist and begin training</li>' : ''}
        </ul>
      </div>
      ` : `
      <div class="next-steps" style="background-color: #e8f5e9; border-left-color: #4caf50;">
        <h4 style="color: #4caf50;">🎉 Almost Done!</h4>
        <p>You've completed all major onboarding steps! Please review your onboarding checklist and begin your training.</p>
      </div>
      `}

      <p>You're almost there! Once these final steps are complete, you'll be all set to start your journey with Lendwise.</p>

      <p>If you have any questions about the remaining steps, please don't hesitate to reach out.</p>

      <div class="signature">
        <p>Best regards,</p>
        <p class="signature-name">${recruiterName}</p>
        <p style="margin: 0; color: #666;">Lendwise Mortgage</p>
      </div>
    </div>

    <div class="footer">
      <p>This email was sent from a secure Lendwise account.</p>
      <p>© ${new Date().getFullYear()} Lendwise Mortgage. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
  }
}
